import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TabDataItem } from 'app/shared-components/Types/Types';

interface TabsState {
  saleTabData: TabDataItem[];
  ticketsTabData: TabDataItem[];
  messagingRulesTabData: TabDataItem[];
  selectedSalesTabId: number;
  selectedTicketsTabId: number;
  selectedMessagingRulesTabId: number;
}

const initialState: TabsState = {
  saleTabData: [
    {
      name: 'All',
      id: 1,
      baseFilter: { stageId: '2' },
      sortFilter: { sortField: 'nextActivityTime', sortDirection: 'asc' },
    },
    {
      name: 'Live',
      id: 2,
      baseFilter: { isLive: true },
      sortFilter: { sortField: 'nextActivityTime', sortDirection: 'asc' },
    },
    {
      name: 'Muted',
      id: 7,
      baseFilter: { isMuted: true },
      sortFilter: { sortField: 'nextActivityTime', sortDirection: 'desc' },
    },
    {
      name: 'Active',
      id: 3,
      baseFilter: { stageId: '2', statusId: 'null' },
      sortFilter: { sortField: 'nextActivityTime', sortDirection: 'asc' },
    },
    {
      name: 'Hold',
      id: 4,
      baseFilter: { stageId: '2', statusId: '1' },
      sortFilter: { sortField: 'nextActivityTime', sortDirection: 'asc' },
    },
    {
      name: 'Snooze',
      id: 5,
      baseFilter: { stageId: '2', statusId: '2' },
      sortFilter: { sortField: 'nextActivityTime', sortDirection: 'asc' },
    },
    {
      name: 'Action Required',
      id: 6,
      extraFilter: { name: 'status', value: 'AR' },
      sortFilter: { sortField: 'nextActivityTime', sortDirection: 'asc' },
    },
  ],
  ticketsTabData: [
    { name: 'All', id: 1, baseFilter: {} },
    { name: 'Open', id: 2, baseFilter: { statusId: '1' } },
    { name: 'In Progress', id: 3, baseFilter: { statusId: '2' } },
    { name: 'Closed', id: 4, baseFilter: { statusId: '3' } },
  ],
  messagingRulesTabData: [
    { name: 'All', id: 0 },
    { name: 'Creation', id: 1, baseFilter: { trigger: '1' } },
    { name: 'Leads to Sales', id: 2, baseFilter: { trigger: '2' } },
    { name: 'Sales to Processing', id: 3, baseFilter: { trigger: '3' } },
    { name: 'Processing to Completed', id: 4, baseFilter: { trigger: '4' } },
    { name: 'Cancellation', id: 5, baseFilter: { trigger: '5' } },
    { name: 'Manual Status Update', id: 6, baseFilter: { trigger: '6' } },
    { name: 'Vault Link Text', id: 7, baseFilter: { trigger: '7' } },
    { name: 'Vault Link Email', id: 8, baseFilter: { trigger: '8' } },
    // ForgotPassword (9) is excluded - it's not actively used by the backend
    { name: 'Ticket Assigned', id: 10, baseFilter: { trigger: '10' } },
    { name: 'Ticket Closed', id: 11, baseFilter: { trigger: '11' } },
    { name: 'Insurance Info', id: 12, baseFilter: { trigger: '12' } },
    { name: 'Error in Leads via Email', id: 13, baseFilter: { trigger: '13' } },
    { name: 'Terms and Conditions', id: 14, baseFilter: { trigger: '14' } },
    { name: 'AI Call Scheduling', id: 15, baseFilter: { trigger: '15' } },
  ],
  selectedSalesTabId: 2,
  selectedTicketsTabId: 1,
  selectedMessagingRulesTabId: 0,
};

const tabsSlice = createSlice({
  name: 'tabs',
  initialState,
  reducers: {
    setSelectedTab(state, action: PayloadAction<number>) {
      state.selectedSalesTabId = action.payload;
      state.selectedTicketsTabId = action.payload;
    },
    setSelectedMessagingRulesTab(state, action: PayloadAction<number>) {
      state.selectedMessagingRulesTabId = action.payload;
    },
  },
});

export const { setSelectedTab, setSelectedMessagingRulesTab } = tabsSlice.actions;

export const selectSalesTabs = (state: any) => state.tabs.saleTabData;
export const selectSelectedSalesTabId = (state: any) => state.tabs.selectedSalesTabId;
export const selectTicketsTabs = (state: any) => state.tabs.ticketsTabData;
export const selectSelectedTicketsTabId = (state: any) => state.tabs.selectedTicketsTabId;
export const selectMessagingRulesTabs = (state: any) => state.tabs.messagingRulesTabData;
export const selectSelectedMessagingRulesTabId = (state: any) =>
  state.tabs.selectedMessagingRulesTabId;

export default tabsSlice.reducer;
